/**
 * @keystone/content — OWNER: content builder (packages/content/**).
 * Pure data + small helpers. Exposes the bundle and a ready registry.
 */
import type { ContentBundle, ContentRegistry } from '@keystone/sim';
import { collectMechanicAuras, createRegistry } from '@keystone/sim';
import { TIER_HP } from './balance';
import { BASE_AURAS, CLASSES, SPECS } from './classes/index';
import { trainingGrounds } from './dungeons/trainingGrounds';
import { venomAltar } from './dungeons/venomAltar';
import { BOSS_ABILITIES, BOSS_NPCS, ENCOUNTERS } from './npcs/bosses';
import { TRASH_ABILITIES, TRASH_AURAS, TRASH_NPCS } from './npcs/trash';
import { BARROW_ABILITIES, BARROW_ENCOUNTERS, BARROW_NPCS } from './zones/barrowNpcs';
import { FIELD_AURAS, MOSSVALE_FIELD_MAPS } from './zones/fields';
import { hollowBarrow } from './zones/hollowBarrow';
import { FIELD_MOB_ABILITIES, FIELD_MOB_NPCS } from './zones/mobs';
import { ACOLYTE_NPCS } from './classes/specs/acolyte'; // +acolyte-specs: the Heretic's Hollow One (a pet)
import { BEAST_NPCS } from './classes/specs/ranger'; // +ranger-specs: the spirit beast forms

export * from './ids';
export * from './balance';
export * from './forces';
export { validateBundle, validateDungeon, validateField, validateZone } from './validate';
export { BASE_AURAS, BASE_CLASSES, BASE_CLASS_ART, BASE_SPECS, BASE_VISUAL_ALIAS, CLASSES, LEGACY_CLASSES, type SpecPreview } from './classes/index';
// +specs: specialisations (docs/design/classes/specs.md): the implemented SpecDefs, all 15 previews, helpers.
export {
  ACOLYTE_SPECS, ACOLYTE_SPEC_INFO, BLOODREAVER, BLOODREAVER_AURA, IRONCLAD, IRONCLAD_AURA, MAGE_SPECS, MAGE_SPEC_INFO, RANGER_SPECS, RANGER_SPEC_INFO, RAVAGER, RAVAGER_AURA,
  ROGUE_SPECS, ROGUE_SPEC_INFO, SPECS, SPEC_ABILITIES, SPEC_INFO, SPEC_LEVEL, WARRIOR_SPEC, WARRIOR_SPECS, WARRIOR_SPEC_INFO, bloodreaver, ironclad, ravager, specClass, specDef, specInfo, specsOf,
  type SpecInfo,
} from './classes/index';
// +rogue-specs: Shadowdancer / Mirage / Venomwright ids (classes/specs/rogue.ts).
export { MIRAGE, MIRAGE_AURA, ROGUE_SPEC, ROGUE_ZONE, SHADOWDANCER, SHADOWDANCER_AURA, TWO_TOXINS, VENOMWRIGHT, VENOMWRIGHT_AURA, mirage, shadowdancer, venomwright } from './classes/specs/rogue';
// +mage-specs: Stormcaller / Rimecaller / Cinderborn ids (classes/specs/mage.ts).
export { CINDERBORN, CINDERBORN_AURA, MAGE_SPEC, RIMECALLER, RIMECALLER_AURA, STORMCALLER, STORMCALLER_AURA, cinderbornSpec, rimecallerSpec, stormcallerSpec } from './classes/specs/mage';
// +acolyte-specs: Lightbinder / Hemomancer / Heretic ids (classes/specs/acolyte.ts).
export { ACOLYTE_NPCS, ACOLYTE_SPEC, HEMOMANCER, HEMOMANCER_AURA, HERETIC, HERETIC_AURA, HOLLOW_ONE_NPC, LIGHTBINDER, LIGHTBINDER_AURA, hemomancerSpec, hereticSpec, lightbinderSpec } from './classes/specs/acolyte';
// +ranger-specs: Deadeye / Grovewarden / Beastbound ids (classes/specs/ranger.ts); RANGER_SPEC_NAME holds the display names.
export {
  BEASTBOUND, BEASTBOUND_AURA, BEAST_FORM, BEAST_FORM_LEVEL, BEAST_NPCS, BOND, DEADEYE, DEADEYE_AURA, DEADEYE_RELOAD, GROVEWARDEN, GROVEWARDEN_AURA, GROVEWARDEN_HOTS, RANGER_SPEC, RANGER_SPEC_NAME, ROUNDS,
  beastbound, deadeye, grovewarden,
} from './classes/specs/ranger';
export { WOW_NAMES, ALLOWED_ORIGINAL, GRID_DENYLIST, GRID_DENY_SUBSTRINGS } from './classes/names';
export * from './grid/index'; // +grid: the Waystone Grid
export { VENOM_ALTAR_ROUTE, venomAltar } from './dungeons/venomAltar';
export { trainingGrounds } from './dungeons/trainingGrounds';
// +tutorial: the guided first run's yard (kept out of the main bundle; see dungeons/tutorial.ts).
export { TUTORIAL, TUTORIAL_ABILITIES, TUTORIAL_NPCS, tutorialBundle, tutorialGrounds } from './dungeons/tutorial';
export { pointInPolygon } from './dungeons/geometry';
export { corridorHeightAt, groundHeightAt } from './dungeons/elevation';
export { LEVEL as VENOM_ALTAR_LEVELS } from './dungeons/venomAltarLayout';
// +field: Mossvale (zone maps, mobs, the Hollow Barrow, bounties) and the hero unlock tables.
export * from './zones/index';
export { BLADEMASTER_UNLOCKS, LIGHTBINDER_UNLOCKS, STORMCALLER_UNLOCKS, WARDEN_UNLOCKS } from './classes/unlocks';

const NPCS = [...TRASH_NPCS, ...BOSS_NPCS, ...FIELD_MOB_NPCS, ...BARROW_NPCS, ...ACOLYTE_NPCS, ...BEAST_NPCS]; // +ranger-specs: the Beastbound's spirit beast forms
const ENEMY_ABILITIES = [...TRASH_ABILITIES, ...BOSS_ABILITIES, ...FIELD_MOB_ABILITIES, ...BARROW_ABILITIES];
const ENCOUNTER_DEFS = [...ENCOUNTERS, ...BARROW_ENCOUNTERS];

/**
 * +field: the keystone (M+) dungeons — the ids a key / lobby / rating list should offer. The bundle's
 * `dungeons` also holds the zone maps (town, fields) and levelled dungeons (the Hollow Barrow).
 */
export const MPLUS_DUNGEON_IDS: readonly string[] = [venomAltar.id, trainingGrounds.id];
const bundle: ContentBundle = {
  classes: CLASSES,
  npcs: NPCS,
  enemyAbilities: ENEMY_ABILITIES,
  encounters: ENCOUNTER_DEFS,
  // +field: Venom Altar stays first (clients default to dungeons[0]); then the Mossvale maps.
  dungeons: [venomAltar, trainingGrounds, ...MOSSVALE_FIELD_MAPS, hollowBarrow],
  // +encounters: auras nested in mechanic specs / NPC passives / intermissions are registered by id.
  // +field: plus the field-only party auras (FieldDef.partyAuras).
  auras: [...BASE_AURAS, ...TRASH_AURAS, ...FIELD_AURAS, ...collectMechanicAuras({ enemyAbilities: ENEMY_ABILITIES, npcs: NPCS, encounters: ENCOUNTER_DEFS })],
  tierHp: TIER_HP,
  specs: SPECS, // +specs
};

/** The full content bundle (classes, NPCs, enemy abilities, encounters, dungeons). */
export function getContentBundle(): ContentBundle {
  return bundle;
}

/** Indexed registry ready for createSim. */
export function buildRegistry(): ContentRegistry {
  return createRegistry(getContentBundle());
}

/** Default party for the legacy M+ harness (slot order tank, healer, dps, dps). */
export const DEFAULT_PARTY = ['warden', 'lightbinder', 'blademaster', 'stormcaller'] as const;
/** +classes: the playable base classes (character creation, Group Finder), tank and healer first. */
export const PLAYABLE_CLASSES = ['warrior', 'acolyte', 'rogue', 'mage', 'ranger'] as const;
// +world-resources
export { WORLD_EYE_MIN_KEY, WORLD_OBJECT_KINDS, WORLD_VALUE, type WorldObjectKindDef } from './world/loot';
export { placeWorldObjects, routePolyline, worldPlacementProblems, type BossClear, type RoomWorldSpec } from './world/placement';
export { VENOM_ALTAR_WORLD, venomAltarBossClears } from './dungeons/venomAltarWorld';
// +personas (shared contract: docs/design/idle.md)
export type { Archetype, Persona, PersonaId, PersonaMode, PersonaTraits } from './personas/types';
export { ARCHETYPES, generatePersona, personaPreset, refusesToGroup, type GenerateOpts } from './personas/generate';
export { LINES, fillLine, ordinal, type ChatSituation, type LinePool } from './personas/lines';
export { PERSONA_RATES, personaIncidentModel, type ArchetypeRates, type MixRates, type OutcomeRates, type PersonaRates, type PersonaRunEstimate } from './personas/rates';
